// src/i18n/en.ts
// English Translations

export const en = {
  // ============================================
  // COMMON
  // ============================================
  common: {
    loading: "Loading...",
    error: "Error",
    save: "Save",
    cancel: "Cancel",
    confirm: "Confirm",
    delete: "Delete",
    edit: "Edit",
    back: "Back",
    next: "Next",
    continue: "Continue",
    continueVerb: "Continue",
    close: "Close",
    search: "Search",
    filter: "Filter",
    all: "All",
    yes: "Yes",
    no: "No",
    or: "or",

    retry: "Retry",
    ok: "OK",
    copy: "Copy",

    teams: "teams",
    questions: "questions",
    admin: "Admin",
  },

  // ============================================
  // AUTH
  // ============================================
  auth: {
    login: "Sign in",
    logout: "Sign out",
    loginWithGoogle: "Sign in with Google",
    loginSubtitle: "Sign in with your Google account to get started",
    welcomeBack: "Welcome back!",
  },

  // ============================================
  // DASHBOARD
  // ============================================
  dashboard: {
    title: "Teacher Dashboard",
    welcome: "Hello!",
    teacherFallbackName: "Teacher",

    createGame: "Create new game",
    createGameDesc: "Set up teams and questions to start playing in minutes.",

    library: "Question library",
    libraryDesc: "Browse official and community question sets and import them instantly.",

    myGames: "My games",
    active: "active",
    // Portada: textos que antes estaban escritos a mano en DashboardPage
    tagline: "Where we all learn together",
    arbitrosTitle: "Learning Referees",
    arbLabel: "TWO TEAMS VS. TWO, WITH A TEXT",
    arbDesc: "Teams question and grade each other on a text; the AI referees only when they disagree.",
    semaforoLabel: "THE WHOLE CLASS, IN STAGES",
    semaforoDesc: "A coopetition game for the whole class: first they work in teams, then they all play together.",
    toolsLabel: "TO PREPARE YOUR LESSONS",
    toolsName: "Teacher tools",
    toolsDesc: "Rubrics, attendance sheets, teaching sequences, lesson plans and more.",
    enterCta: "Enter →",
    backHome: "← Home",
    moreInfo: "More info",
    configureSession: "Set up a session →",
    show: "▼ show",
    showAll: "▼ show all",
    hide: "▲ hide",
    levelPrimary: "Primary",
    levelSecondary: "Secondary",
    videoStartPrimary: "▶ Primary — How to start",
    videoStartSecondary: "▶ Secondary — How to start",
    videoWhichLevel: "For which level?",
    videoChange: "Change",
    watchVideo: "How to start? Watch video",
    moreTools: "More teacher tools",
    moreToolsDesc: "Rubric generator, attendance sheets, lesson sequences, annual plans, country comparator and more.",
    gameShared: "Game shared with the community!",
    shareLabel: "Share",
    arbAreaDescs: { exactas: "Mathematics, Biology, Physics, Chemistry and more", sociales: "History, Geography, Philosophy, Economics and more", lenguajes: "Language, English, Literature, Communication and more", arte: "Visual Arts, Music, Theater, Physical Education and more" },

    noGames: "No active games",
    startFirst: "Create your first game!",

    selectGameMode: "Select game mode",
    selectGameModeSubtitle: "Choose based on your students' age",
    // En la sección dashboard, agrega:
    evaluationsTitle: "Evaluations to review",
    moreGames: "more games",

    // Games list
    loadingGames: "Loading games...",
    noGamesCreated: "You haven't created any games yet.",
    finishedGames: "finished games",
    viewResults: "View results",
    goToPodium: "Go to podium",
    unnamedGame: "Untitled game",

    // Current mode panel
    currentMode: "Current mode",

    // Footer
    aboutThisProject: "About this project",

    // Status labels (GameCard)
    status: {
      finished: "Finished",
      stage0Proposals: "Stage 0 — Proposals",
      collectingProposals: "Collecting proposals",
      curatingProposals: "Curating proposals",
      stage1Playing: "Stage 1 — Playing",
      transitionStage2: "Preparing Stage 2",
      stage2Playing: "Stage 2 — Playing",
      preparing: "Preparing",
    },

    // Relative time (GameCard)
    time: {
      minutesAgo: (n: number) => `${n} min ago`,
      hoursAgo: (n: number) => `${n} h ago`,
      daysAgo: (n: number) => `${n} d ago`,
    },
  },

  // ============================================
  // JOIN (STUDENTS)
  // ============================================
  join: {
    appTitle: "Traffic Light Game",

    // Top text
    welcomeBack: "Welcome back!",
    enterRoomCode: "Enter the room code",
    selectTeam: "Choose your team",

    // Saved session
    previousSessionFound: "Previous session found",
    codeLabel: "Code",
    reconnecting: "Reconnecting...",
    goBackToGame: "↩️ Back to the game",
    newSession: "New",

    // Divider
    orEnterNewCode: "or enter a new code",

    // Errors
    couldntReconnect: "Couldn't reconnect. Please enter the room code.",
    codeMustBe6: "The code must be 6 characters",
    invalidCode: "Invalid code",
    lookupError: "Error looking up the code. Please try again.",
    noTeamsConfigured: "This game has no teams configured",

    // Buttons / actions
    searching: "Searching...",
    findRoom: "Find room →",
    codeOnTeacherScreen: "The code is on the teacher's screen",

    // Stage / phase banners
    proposalsActive: "Proposals stage is active",
    proposalsDesc: "Your team will create questions for the game",
    waitingTeacher: "Waiting for the teacher",
    waitingDesc: "The game is being prepared",

    // Navigation
    changeCode: "← Change code",

    // Join button texts by phase
    joinProposals: "Create proposals! 📝",
    joinWaiting: "Enter room 🚪",
    joinNormal: "Join! 🎮",
  },

  // ============================================
  // GAME MODES
  // ============================================
  gameModes: {
    trafficLight: {
      title: "Traffic Light Game",
      subtitle: "The Traffic Light Game",
      description: "Ideal for elementary students. Colorful and friendly interface.",
      ageRange: "Under 13",
    },
    coopetition: {
      title: "The Coopetition Game",
      subtitle: "Where competition meets collaboration",
      description: "For teenagers and adults. More professional and sober design.",
      ageRange: "13+ years",
    },
  },

  // ============================================
  // SETUP
  // ============================================
  setup: {
    title: "Set up new game",
    step1: "Step 1: Basic information",
    step2: "Step 2: Questions",
    step3: "Step 3: Set up teams",
    step4: "Step 4: Ready to play!",

    // Step 1
    language: "Language",
    spanish: "Spanish",
    english: "English",
    className: "Class name",
    classNamePlaceholder: "3A - Math",
    subject: "Subject",
    subjectPlaceholder: "Mathematics",
    numTeams: "Number of teams",
    studentsPerTeam: "Students per team",

    // Stage 0
    stage0Section: "Stage 0: Preparation (optional)",
    stage0Enable: "Enable group preparation stage",
    stage0Desc: "Teams will have access to study material before starting the game.",
    stage0Warning:
      "💡 First time playing? We recommend skipping Stage 0 and using a game from the library. Stage 0 is designed for students who already understand the game dynamics.",
    stage0MaterialType: "Material type",
    stage0MaterialLink: "External link",
    stage0MaterialText: "Text",
    stage0MaterialTitle: "Material title",
    stage0MaterialContent: "Content",
    stage0MaterialLinkPlaceholder: "https://docs.google.com/...",
    stage0MaterialTextPlaceholder: "Paste here the text that teams should read...",
    stage0GeneratePrompt: "Need to generate material? Use this prompt with ChatGPT",

    // Step 2
    chooseFromLibrary: "Choose from library",
    orUploadFile: "Or upload your own file:",
    uploadCSV: "Upload CSV file",
    loadedFromLibrary: "Loaded from library:",
    questionsLoaded: "questions loaded",
    analyzing: "Analyzing CSV...",

    // Step 3
    enterNames: "Enter names (one per line)",
    assignRandom: "Assign randomly",
    totalStudents: "Total:",
    uniqueStudents: "Unique:",
    duplicates: "Duplicates:",
    duplicatesNote: "Duplicates are ignored when building teams.",
    duplicatesFound: "Duplicates found:",
    teamsConfigured: "Teams configured",
    moveStudentsHint: "You can move students between teams by selecting the destination team",

    // Step 4
    roomCode: "Room code",
    shareCode: "Share this code with your students",
    stage0Enabled: "Stage 0 enabled. Teams will see the preparation material.",
    startGame: "Start game",
    createGame: "Create game",

    // ========== NEW KEYS FOR THE TOOLTIP ==========
    selfEvaluationTooltip: '💡 Important tip: Before starting the game, tell your students to keep a mental record of who helped them and whom they helped. This significantly improves the quality of the answers in the self-assessment.',
    selfEvaluationGuideLink: '📖 See the full guide',
  },

  // ============================================
  // LIBRARY
  // ============================================
  library: {
    title: "Library",
    pageTitle: "Question banks",
    pageSubtitle: "Explore questions organized by level, area and subject.",

    // Filters
    game: "Game",
    grade: "Grade",
    area: "Area",
    subject: "Subject",
    searchPlaceholder: "Search by title, topic or content...",
    showing: "Showing",
    of: "of",
    items: "items",

    // Card
    use: "Use",
    moreInfo: "More info",
    lessInfo: "Less",
    contents: "Contents",
    skills: "Skills",
    questions: "questions",
    noFile: "No file",
    loading: "Loading...",

    // Stats
    withFile: "with file",

    // Empty
    noResults: "No items found with the selected filters.",

    // Admin
    adminTitle: "Administration",
    adminDesc: "As an administrator, you can upload new question banks.",
    uploadSingle: "Upload single",
    bulkUpload: "Bulk upload",
  },

  // ============================================
  // STAGE 0 - QUESTION PROPOSALS
  // ============================================
  stage0: {
    // Titles
    title: "Stage 0: Preparation",
    subtitle: "Read the material before starting the game",
    teacherTitle: "Stage 0 panel",
    teacherSubtitle: "Review team proposals",

    // Material
    teamName: "Team:",
    materialTitle: "Study material",
    openLink: "Open material",
    noMaterial: "The teacher has not uploaded preparation material.",
    readCarefully: "Read carefully—this content will help you in the game.",

    // Phases
    phaseReading: "Phase: Reading",
    phaseProposing: "Phase: Proposals",
    phaseReviewing: "Phase: Review",
    phaseResults: "Phase: Results",

    // Teacher actions
    startProposals: "Start proposals",
    startReview: "Start review",
    finishAndStart: "Finish and start Stage 1",

    // Proposals - Form
    proposalsTitle: "Propose questions",
    proposalsSubtitle: "Propose questions based on the material",
    proposalCount: "proposals",
    addProposal: "Add question",
    proposalType: "Question type",
    questionText: "Your question",
    questionPlaceholder: "Write your proposed question...",
    hintLabel: "Hint to answer (optional)",
    hintPlaceholder: "A hint to help think about the answer...",
    relatedTopicLabel: "What topic does it relate to?",
    relatedTopicPlaceholder: "E.g.: Water cycle, Fractions...",
    submittedByLabel: "Who proposes it? (optional)",
    submittedByPlaceholder: "Team member name...",
    submit: "Submit question",

    // Proposal types
    typeComprehension: "📝 Comprehension",
    typeComprehensionDesc: "What does it mean...? / Explain in your own words...",
    typeRelation: "🔗 Connection to other topics",
    typeRelationDesc: "How does it connect to...? / What similarities are there with...?",
    typeApplication: "🌍 Practical application",
    typeApplicationDesc: "Where do you see this in real life? / How would you use...?",
    typeAnalysis: "🤔 Analysis / Opinion",
    typeAnalysisDesc: "Why do you think...? / What would happen if...?",
    typeProduction: "💡 Production",
    typeProductionDesc: "Draw / Represent / Order the steps...",

    // Proposal status
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    edited: "Edited",

    // Team ready
    markReady: "We finished proposing",
    unmarkReady: "We want to keep proposing",
    teamReady: "Team ready!",
    waitingReview: "Waiting for the teacher to review proposals...",
    waiting: "Waiting for the teacher to start the game...",

    // Timer
    timeRemaining: "Time remaining",

    // Results
    resultsTitle: "Stage 0 results",
    approvedCount: "approved questions",
    bonusPoints: "bonus points",
    waitingStart: "Waiting for the teacher to start Stage 1...",

    // Errors
    errorEmpty: "Write a question",
    errorRelated: "Indicate what topic it relates to",
    maxReached: "Maximum proposals reached",

    // Teacher panel - Filters
    filterAll: "All",
    filterPending: "Pending",
    filterApproved: "Approved",
    filterRejected: "Rejected",
    allTeams: "All teams",

    // Teacher panel - Stats
    totalProposals: "Total proposals",
    pendingCount: "Pending",
    approvedCountLabel: "Approved",
    rejectedCount: "Rejected",
    teamsReady: "Teams ready",

    // Teacher panel - Actions
    proposedBy: "Proposed by",
    relatedTo: "Related to",
    hint: "Hint",
    approve: "Approve",
    reject: "Reject",
    edit: "Edit",
    saveEdit: "Save",
    cancelEdit: "Cancel",
    bonusLabel: "Bonus points",

    // Teacher panel - Empty
    noProposals: "No proposals yet",
    noProposalsFiltered: "No proposals match these filters",

    // Teacher panel - Confirmations
    pendingWarning: "There are pending proposals to review",
    confirmStart: "Start Stage 1? Bonus points will be applied automatically.",

    // Save to library
    saveToLibrary: "Save to my library",
    savedToLibrary: "Saved to library!",

    // Setup
    enableProposals: "Enable question proposals",
    enableProposalsDesc:
      "Teams can propose questions based on the material. You decide which to approve and how many points to assign.",
    maxProposalsPerTeam: "Max questions per team",
    timeLimitOptional: "Time limit (optional)",
    noLimit: "No limit",
  },

  // ============================================
  // GAME
  // ============================================
  game: {
    stage1: "Stage 1: Individual rating",
    stage2: "Stage 2: Group collaboration",
    round: "Round",
    question: "Question",
    hint: "Hint",
    timeLeft: "Time left",
    submit: "Submit",
    correct: "Correct!",
    incorrect: "Incorrect",
    waiting: "Waiting...",
    results: "Results",
    winner: "Winner!",
    score: "Score",
    team: "Team",
    player: "Player",
  },

  // ============================================
  // ERRORS
  // ============================================
  errors: {
    generic: "Something went wrong. Please try again.",
    loadingCSV: "Error loading file. Please try again.",
    creatingGame: "Error creating game.",
    startingGame: "Error starting game.",
    completeFields: "Complete class name and subject.",
    uploadCSV: "Upload a CSV file with questions.",
    assignStudents: "Assign students to teams.",
    enterStudentNames: "First enter student names.",

    deletingGame: "Error deleting game.",
    notAuthorized: "You don't have permission to do this.",
    notFound: "Not found.",
  },

  // ============================================
  // GRADES
  // ============================================
  grades: {
    "3°": "3rd Grade",
    "4°": "4th Grade",
    "5°": "5th Grade",
    "6°": "6th Grade",
    "7°": "7th Grade",
    secondary: "Secondary",
  },

  // ============================================
  // CSV PARSER
  // ============================================
  csv: {
    warnings: {
      noValidHeadersFallback:
        "ℹ️ This CSV had no valid headers. Columns were interpreted by position and IDs were generated if missing.",
      encodingIssuesPossible:
        "⚠️ The file might have encoding issues (incorrect accents). Consider saving it as UTF-8.",
      repeatedHeaderIgnored: "Row {{rowNumber}}: repeated header (ignored).",
      missingQuestionTextIgnored: "Row {{rowNumber}}: missing question text (ignored).",
      invalidSuggestedStageDefaulted:
        "Row {{rowNumber}}: invalid suggestedStage (\"{{value}}\"). Stage {{defaultStage}} was used.",
    },
    errors: {
      emptyCsvFile: "The CSV file is empty.",
      noValidQuestionsFound: "No valid questions were found in the CSV.",
      papaparseError: "Error reading CSV: {{message}} (row {{row}}).",
      cannotReadCsv: "Could not read CSV: {{message}}.",
      cannotReadCsvFallback: "Could not read CSV (fallback): {{message}}.",
    },
  },

  // ============================================
  // CSV PREVIEW
  // ============================================
  csvPreview: {
    title: "Import preview",
    totalRows: "Total rows",
    validQuestions: "Valid questions",
    warnings: "Warnings",
    errors: "Errors",
    validQuestionsTitle: "Valid questions",
    editBeforeImport: "You can edit before importing",
    question: "Question",
    hint: "Hint",
    noHint: "No hint",
    stage: "Stage",
    cancel: "Cancel",
    import: "Import",
    questions: "questions",
    noValidQuestions: "No valid questions",
    note: "Only valid questions will be imported. Rows with errors will be ignored.",
  },

  // ============================================
  // GAME CONTROLLER
  // ============================================
  gameController: {
    loading: "Loading game...",
    error: "Error",
    gameNotFound: "Could not load game",
    teamNotFound: "Team not found",
    waiting: "Waiting",
    preparingRound: "Preparing next round...",
    viewTeam: "View team",
    viewClassroom: "Classroom view",
    startingStage2: "Starting Stage 2...",
    errorStartingStage2: "Error starting Stage 2",
    resetConfirm: "Reset ALL teams to round 1? They will lose all progress.",
    resetSuccess: "Game reset. All teams return to round 1.",
    resetError: "Error resetting game",
  },

  // ============================================
  // SOUND
  // ============================================
  sound: {
    enable: "Enable sounds",
    disable: "Disable sounds",
    on: "ON",
    off: "OFF",
  },

  // ============================================
  // ADMIN
  // ============================================
  admin: {
    title: "Admin metrics",
    downloadExcel: "Download Excel",
    teachers: "Teachers",
    totalSessions: "Total sessions",
    gamesCreated: "Games created",
    email: "Email",
    uid: "UID",
    sessions: "Sessions",
    games: "Games",
    lastLogin: "Last login",
    lastAccess: "Last access",
    noData: "No teacher data yet",
    backToSetup: "Back to setup",
    accessDenied: "Access denied",
    authDisabled: "Authentication system is disabled.",
    enableAuth: "Enable VITE_AUTH_REQUIRED=true to access metrics.",
    verifyingSession: "Verifying session...",
    verifyingPermissions: "Verifying admin permissions...",
    restrictedAccess: "Restricted access",
    adminsOnly: "This page is for administrators only.",

    // Upload page
    uploadTitle: "Upload new CSV",
    bankTitle: "Question bank title",
    bankTitlePlaceholder: "E.g.: Vertebrates and Invertebrates",
    grade: "Grade",
    subject: "Subject",
    description: "Content description",
    descriptionPlaceholder: "E.g.: Animal classification by vertebral column...",
    csvFile: "CSV file",
    csvValid: "Valid CSV",
    file: "File",
    questionsDetected: "Questions detected",
    completeAllFields: "Complete all fields",
    uploadToLibrary: "Upload to library",
    uploading: "Uploading...",
    uploadSuccess: "CSV uploaded successfully!",
    redirecting: "Redirecting to library...",
  },

  // ============================================
  // TEACHER LIBRARY
  // ============================================
  teacherLibrary: {
    myGames: "My games",
    community: "Community",
    official: "Official library",

    privateGames: "Private games",
    publicGames: "Public games",
    totalUses: "Total uses",
    avgRating: "Average rating",

    saveGame: "Save game",
    editGame: "Edit game",
    deleteGame: "Delete game",
    copyGame: "Copy to my library",
    makePublic: "Make public",
    makePrivate: "Make private",
    useGame: "Use this game",

    private: "Private",
    public: "Public",
    onlyYou: "Only you can see this game",
    everyoneCanSee: "Visible to the entire community",

    rate: "Rate",
    ratings: "ratings",
    noRatingsYet: "No ratings yet",
    yourRating: "Your rating",
    thankYou: "Thanks for rating!",
    cantRateOwn: "You can't rate your own game",

    report: "Report",
    reportGame: "Report game",
    reportReason: "Report reason",
    inappropriate: "Inappropriate content",
    incorrect: "Incorrect information",
    spam: "Spam",
    copyright: "Copyright violation",
    other: "Other",
    reportDetails: "Details (optional)",
    reportSubmitted: "Report submitted. Thanks for helping maintain the community.",
    alreadyReported: "You already reported this game",

    copySuccess: "Game copied to your library!",
    copyAsPrivate: "Saved as private. You can edit and publish it whenever you want.",

    privateLimitReached: "You've reached the private games limit",
    privateLimitDesc: "Make a game public or delete one to create more.",

    noMyGames: "You haven't saved any games yet",
    noMyGamesDesc: "Create a game and save it for later use",
    noCommunityGames: "No community games with these filters",
    noCommunityGamesDesc: "Try changing filters or be the first to share",

    sortBy: "Sort by",
    recent: "Most recent",
    rating: "Best rated",
    popular: "Most used",

    gameTitle: "Game title",
    gameTitlePlaceholder: "E.g.: Fractions Quiz",
    gameDescription: "Description",
    gameDescriptionPlaceholder: "Describe what the game is about and who it's for...",
    selectVisibility: "Do you want to share it with the community?",
    visibilityNote: "You can change visibility later",

    confirmDelete: "Are you sure you want to delete this game?",
    confirmDeleteDesc: "This action cannot be undone.",
    confirmMakePublic: "Publish this game?",
    confirmMakePublicDesc: "It will be visible to all teachers in the community.",

    by: "by",
    you: "you",

    timesUsed: "times used",
    timesCopied: "times copied",
  },

  // ============================================
  // PROMPT GENERATOR
  // ============================================
  promptGenerator: {
    title: "AI prompt generator",
    subtitle: "Create an optimized prompt to generate educational questions",

    step1: "Level & configuration",
    step2: "What topic?",
    step3: "Advanced settings",

    level: "Education level",
    primary: "Elementary",
    secondary: "Secondary/High School",
    grade: "Grade/Year",

    promptLanguage: "Game language",
    spanish: "Spanish",
    english: "English",

    category: "Content category",
    subject: "Curricular subject",
    history: "Historical event",
    book: "Book",
    fun: "Movie/Series",

    selectSubject: "Select a subject",
    selectEvent: "Select a historical event",
    selectBook: "Select a book",
    selectMedia: "Select a movie or series",
    other: "Other (specify)",
    customPlaceholder: "Write the topic...",

    showAll: "Show all",
    forPrimary: "For elementary",
    forSecondary: "For secondary",
    universal: "Universal",
    argentina: "Argentina",
    americas: "Americas",
    movies: "Movies",
    series: "Series",
    documentaries: "Documentaries",

    advancedSettings: "Advanced settings",
    totalQuestions: "Number of prompts",
    stage1Production: "% Production in Stage 1",
    stage2Production: "% Production in Stage 2",
    subtopicsInclude: "Subtopics to INCLUDE",
    subtopicsExclude: "Subtopics to EXCLUDE",
    subtopicsPlaceholder: "Separate with commas...",
    preferredStyle: "Preferred style",
    practical: "More practical",
    analytical: "More analytical",
    mixed: "Mixed",

    stageExplanation: "What are Stage 1 and Stage 2?",
    stage1Title: "Stage 1 - Internal preparation",
    stage1Desc:
      "NOT competitive. Each team works internally to level up. Prompts are introductory and diagnostic. Mistakes are opportunities for group learning.",
    stage2Title: "Stage 2 - Collaborative competition",
    stage2Desc:
      "Competition between teams. More challenging and production-focused prompts. Apply knowledge built in Stage 1. Internal collaboration + external competition.",

    generatePrompt: "Generate prompt",
    copyToClipboard: "Copy to clipboard",
    openInGemini: "Open in Gemini",
    copied: "Copied!",

    savedPresets: "Saved configurations",
    savePreset: "Save configuration",
    presetName: "Configuration name",
    presetNamePlaceholder: "E.g.: Math 6th grade",
    loadPreset: "Load",
    deletePreset: "Delete",
    noPresets: "No saved configurations",
    maxPresetsReached: "Maximum 5 configurations. Oldest will be removed.",
    presetSaved: "Configuration saved!",

    previewTitle: "Prompt preview",
    promptReady: "Your prompt is ready",
    promptReadyDesc:
      "Copy and paste it into Gemini, ChatGPT or another AI assistant to generate the prompts.",

    selectContent: "Select content to continue",
    portuguese: "Portuguese",
  },

  // ============================================
  // PROTECTED ROUTE
  // ============================================
  protectedRoute: {
    verifyingSession: "Verifying session...",
  },
  // ============================================
  // REFERRAL PAGE
  // ============================================
  referral: {
    title: "Refer a colleague",
    subtitle: "Help us grow by sharing the Traffic Light Game with other educators",
    referredName: "Colleague's name",
    referredEmail: "Colleague's email",
    referredSchool: "School/Institution",
    message: "Personal message (optional)",
    messagePlaceholder: "Why do you think they'd be interested?",
    submit: "Submit referral",
    sending: "Sending...",
    successTitle: "Thank you for your referral!",
    successMessage: "We'll contact your colleague. When they sign up, you'll receive your reward.",
    rewards: "Your reward",
    rewardsDetail: "🎴 PDF Card Kit + 🌟 Collaborator Badge",
    errorMessage: "There was an error. Please try again.",
    backToDashboard: "Back to home",
  },

  // ============================================
  // WEBINAR PAGE - ENGLISH
  // ============================================
  // Reemplazar la sección "webinar" en src/i18n/en.ts

  webinar: {
    title: "Webinar Registration",
    noWebinar: "No webinars scheduled",
    noWebinarDesc: "Check back soon for upcoming events.",
    dateLabel: "Date and time",
    timezone: "(Argentina, GMT-3)",
    name: "Your name",
    email: "Your email",
    school: "School/Institution (optional)",

    // New field: teaching level
    teachingLevel: "What level do you teach?",
    levelPrimary: "Primary / Elementary",
    levelSecondary: "Secondary / High School",
    levelHigher: "Higher Education / University",
    levelOther: "Other",

    // Experience - improved text
    experience: "Experience with the Traffic Light Game",
    experienceNone: "Never used it",
    experienceExplored: "I checked it out but haven't tried it in class",
    experienceUsed: "I've used it in class",
    experienceRegular: "I use it regularly",

    questions: "What would you like to learn? (optional)",
    questionsPlaceholder: "Topics you're interested in or questions you have...",
    submit: "Register",
    sending: "Registering...",

    // Success screen - improved
    successTitle: "You're registered!",
    successMessage: "We'll send you the access link by email.",
    successReminder: "In the meantime, you can try the game:",
    successLinkPrimary: "🚦 Primary",
    successLinkSecondary: "🎯 Secondary",

    errorMessage: "There was an error. Please try again.",
    backToDashboard: "Back to home",
  },

  // ========== SECTION 13: Preparation for Self-Assessment ==========
  s13_title: '13. Preparation for Self-Assessment',
  s13_warning: '⚠️ If you are going to enable self-assessment, it is essential to prepare students BEFORE starting the game.',
  s13_before_title: 'What to do BEFORE the game',
  s13_before_list1: 'Explain to students that at the end they will need to reflect on the process',
  s13_before_list2: 'Ask them to keep a "mental log" or brief notes:',
  s13_before_sub1: 'Who helped me understand something?',
  s13_before_sub2: 'Who did I help?',
  s13_before_sub3: 'Which concept was hardest for me?',
  s13_before_sub4: 'How did I feel working in a team?',
  s13_during_title: 'What to do DURING the game',
  s13_during_list1: 'You can have a visible board with the reflection questions',
  s13_during_list2: 'Briefly remind between stages to observe interactions',
  s13_when_use_title: 'When to USE self-assessment',
  s13_use_1: '✅ Topics that require deep understanding',
  s13_use_2: '✅ When there is enough time (add 10-15 min at the end)',
  s13_use_3: '✅ Groups that already know the game dynamics',
  s13_use_4: '✅ When you want to identify "experts" for the pedagogical closing',
  s13_when_not_use_title: 'When NOT to use self-assessment',
  s13_not_use_1: '❌ First time the group plays',
  s13_not_use_2: '❌ Introductory or quick review topics',
  s13_not_use_3: '❌ When time is short',
  s13_not_use_4: '❌ Very large groups where closing would be hard to manage',
  s13_tip: '💡 Self-assessment identifies students most mentioned as "helpers." These students can explain key concepts to the rest of the group during the pedagogical closing, reinforcing their own learning and that of their peers.',

  // ============================================
  // REFEREE — accessibility (batches 0 and 1)
  // Screen-reader texts, color names and new messages.
  // ============================================
  arbitro: {
    teamColors: {
      red: "Red",
      blue: "Blue",
      green: "Green",
      amber: "Amber",
      violet: "Violet",
      pink: "Pink",
      teal: "Teal",
      orange: "Orange",
    },
    teamColorGroup: "Color for team {team}",
    teamColorNumber: "{n}",
    timerName: "Question timer",
    timerLastSeconds: "⚠️ Last 15 seconds!",
    timerAnnounce15: "15 seconds left",
    timerAnnounceUp: "Time is up",
    soundOff: "Mute the timer sound",
    soundOn: "Turn the timer sound on",
    rulesStep5Who: "The AI reads both teams' arguments",
    rulesStep5Desc: "The referee (AI) reads both teams' written arguments and rules by citing the source text. Its ruling is final for that round.",
    rulesStep5Info: "Teams grade each other. If the other team does not accept the grade, both write their arguments and the referee (AI) splits the point citing the source text. Its split is final for that round.",
    rulesStep5Template: "5. If the other team does not accept the grade, both teams write their arguments and the referee (AI) splits the point citing the source text, from inside the game. Its split is final for that round.",
    errorConsultFailed: "We could not reach the referee. Check your connection and try again.",
    errorRetryHint: "The referee's split is final for each round: if it could not be reached, try again.",
    offTopicQuestion: "The question is outside the text: choose another one",
    offTopicChooseAnother: "Choose another question",
    offTopicLabel: "Off topic",
    offTopicNoPoints: "This round gives no points and does not count: choose another question.",
    resumeQuestion: "There is a game in progress: continue or start over?",
    resumeSummary: "{team1} {points1} · {team2} {points2} — round {round} of {total}",
    resumeContinue: "Continue",
    resumeRestart: "Start over",
    resumeOtherTab: "If this game is open in another tab, continue there.",
    ownQuestionOffTopic: "That question isn't in the text: write another one or pick one from the list",
    ownQuestionChecking: "Checking the question…",
    allowOwnQuestions: "Allow custom questions",
    allowOwnQuestionsHint: "Teams can write their own question as well as pick one from the list. Before it goes to the other team, it is checked against the text and the topic.",
    cfgMetaMode: "Metacognition report",
    cfgMetaGroup: "Group (per team)",
    cfgMetaIndividual: "Individual (per student)",
    cfgMetaGroupHint: "Each team writes a single reflection.",
    cfgMetaIndividualHint: "Each student answers in turn on the group's device. You see a breakdown per student.",
    cfgMetaIndividualWarning: "On the shared screen, teammates see what each student writes.",
    cfgMetaQTitle: "Metacognition questions (advanced)",
    cfgMetaQHint: "If you do not change them, these 4 questions are used. You can edit them, add or remove (1 to 6). If you write {tema}, it is replaced by the session topic. Students must answer the first one.",
    cfgMetaQN: "Question {n}",
    cfgMetaQAdd: "Add question",
    cfgMetaQRemove: "Remove",
    cfgMetaQRemoveLabel: "Remove question {n}",
    cfgMetaQReset: "Reset to default questions",
    cfgCriteriosTitle: "Grading criteria (optional)",
    cfgCriteriosHint: "They apply to every question in the session. In both modes, teams use them to answer and to grade, and the referee uses them to settle disputes. In written mode, the AI also uses them to review every answer. We suggest agreeing on them with the class before playing.",
    cfgCriterioPropio: "Add your own",
    cfgAnswerMode: "Answer mode",
    cfgAnswerOralTitle: "Quick review",
    cfgAnswerWrittenTitle: "Full record",
    cfgAnswerOralSummary: "Teams answer out loud. Only disputes are recorded.",
    cfgAnswerWrittenSummary: "Teams type every answer. Slower, but everything is saved.",
    cfgAnswerMoreDetail: "More detail",
    cfgAnswerOralHint: "Teams answer out loud: faster and more dynamic. You see details only for rounds that went to a dispute. If a team accepts a wrong answer, it isn't recorded. Best for review, debate, or short lessons.",
    cfgAnswerWrittenHint: "Every answer is typed and saved. At the end, the AI reviews the answers nobody disputed and flags the errors that slipped through, so you can decide. Slower. Best for precise content, or when you want to see what each team answered.",
    cfgAnswerWrittenShortTimeWarning: "With 1 minute per round, there may not be enough time to write the answer.",
    roundsTitle: "Session rounds",
    roundsLoading: "Loading rounds…",
    roundsError: "The rounds couldn't be loaded.",
    gameN: "Game {n}",
    roundLabel: "Round {n}",
    splitLabel: "Split",
    panAnswerLabel: "Answer:",
    verdictLabel: "Referee's verdict",
    explanationLabel: "Explanation",
    indAnswersCount: "Answers: {n}",
    positionsLabel: "See both positions",
    passedLabel: "Skipped",
    correctedByTeacher: "Corrected by the teacher",
    correctedDetail: "Referee: {original} → Teacher: {nuevo} ({fecha})",
    correctSplit: "Correct the split",
    correctSave: "Save correction",
    correctSaving: "Saving…",
    correctError: "The correction couldn't be saved. Please try again.",
    correctOnlyOwner: "Only the teacher who owns the session can correct the splits.",
    // AI review of non-disputed written answers (Stage 2 of the Learning Referees).
    revTitle: "🔎 AI review",
    revChecking: "Reviewing…",
    revError: "Couldn't review this game.",
    revRetry: "Retry",
    revFlaggedCount: (n: number): string => (n === 1 ? "1 case to review" : `${n} cases to review`),
    revNoneFlagged: "No cases to review",
    revNotRevisableCount: (n: number): string => (n === 1 ? "1 round couldn't be reviewed" : `${n} rounds couldn't be reviewed`),
    revNotRevisable: "The AI couldn't produce a verdict for this round after several attempts. Please review it by hand.",
    revShowDetails: (n: number): string => `Show details (${n} reviewed)`,
    revHideDetails: "Hide details",
    revAiVerdictLabel: "The AI suggests:",
    revComparisonErrorNotCaught: "The other team graded it higher than the AI: a mistake may have gone unnoticed.",
    revComparisonNotDefended: "The other team graded it lower than the AI: the answer may be better than it was given credit for.",
    revComparisonNone: "Matches the other team's grade.",
    revConfirm: "Confirm",
    revCorrect: "Correct it",
    revDiscard: "Dismiss",
    revCancel: "Cancel",
    revNoteLabel: "Note (replaces the AI's explanation)",
    revNotePlaceholder: "Explain which grade is right and why…",
    revNoteRequired: "A note is required to correct a verdict.",
    revSaving: "Saving…",
    revSaveError: "Couldn't save. Try again.",
    revStatusConfirmed: "✅ Confirmed by the teacher",
    revStatusCorrected: "🛠️ Corrected by the teacher",
    revStatusDiscarded: "🚫 Dismissed by the teacher",
    revAiExplanationLabel: "AI explanation",
    revTeacherNoteLabel: "Teacher's note",
    revVerdictLabel: "Verdict",
    // Already-saved verdict of a corrected observation, shown next to the teacher's note (not the same as
    // revVerdictLabel, which labels the selector while the form is still being filled in).
    revTeacherVerdictLabel: "Teacher's verdict",
    revVerdictChoose: "Choose a verdict…",
    revVerdictRequired: "Choose a verdict to correct this.",
    revNoteGoesInCoursePdf: "This note appears in the course version of the report.",
    revCompleteVerdictBanner: "This observation was corrected before a verdict was required. Complete it so it can appear in the course PDF.",
    revCompleteVerdict: "Complete verdict",
    revGeneralNoteLabel: "General note for this game",
    revGeneralNotePlaceholder: "General comment about this game (optional)…",
    revGeneralNoteSaving: "Saving…",
    revGeneralNoteSaved: "General note saved.",
    // Stage 3: course PDF (plan a/c/d/e/f) — per-game button, file name and a warning about unreviewed
    // observations before generating (they won't appear in the PDF).
    panGenerateCoursePdf: "📘 PDF for the course",
    panJugadoConTexto: "📖 Played with the text printed.",
    panJugadoSinTexto: "📖 Played without the text.",
    panCriteriosLabel: "Grading criteria:",
    panPdfCourseSuffix: "Course",
    revCoursePdfPendingTitle: "Unreviewed observations",
    revCoursePdfPendingDesc: (n: number): string => (n === 1
      ? "There's 1 unreviewed observation in this game: it won't appear in the course PDF. Generate it anyway?"
      : `There are ${n} unreviewed observations in this game: they won't appear in the course PDF. Generate it anyway?`),
    revCoursePdfGenerateAnyway: "Generate anyway",
    // Adjustments (2) and (4) to the course PDF: the grade the other team gave (so "error not caught" / "answer
    // not defended" make sense without the score) and a note that the teacher corrected a dispute's ruling (no
    // numbers: the course PDF carries no scores).
    revGradedAsLabel: "Other team's grade",
    revRefereeRulingCorrected: "The teacher corrected the referee's ruling.",
    optTotal: "{team} is entirely right",
    optPartial: "{team} is mostly right",
    optBoth: "Both are partly right",
    optNone: "Neither is right",
    totalCorrected: "Corrected total",
    reportCorrectionsTitle: "Teacher's corrections",
    reportCorrectedRounds: "Rounds corrected by the teacher: {n} of {total}",
    reportBeforeCorrections: "Report generated before the corrections",
    pointsAbbrev: "pts",
    correctionNote: "The correction does not change the score the students already saw.",
    aiUnavailable: "The AI service is not available right now, please try again in a few minutes.",
    errorNoClearAnswer: "The referee could not give a clear answer. No points were added. Please try again.",
    closeConfig: "Close the setup and go back",
    closeDialog: "Close the dialog",
    questionsFieldName: "Game questions, one per line",
    questionsCountName: "Number of questions to generate",
    teamPoints: "{points} points",
    loginToGenerateQuestions: "Sign in with Google to generate questions",
    loginWithGoogle: "Sign in with Google",
    loginToGenerateQuestionsFailed: "Couldn't sign in. Please try again.",
    questionTypeLegend: "Type of questions to generate",
    required: "required",
    themeRequiredHint: "Enter the topic to continue.",
    charsNearLimit: "Close to the character limit",
    charsOverLimit: "Over the limit: the text will be cut off when sent to the referee",
    configFormTitle: "Session setup",
    sessionCreatedAnnounce: "Session created. The student link is below.",
    generatingQuestions: "Generating questions…",
    gameScreenSetup: "Set up the game",
    gameScreenAsk: "Question turn",
    gameScreenGrade: "Grading the answer",
    gameScreenAccept: "Accept the grade?",
    gameScreenDispute: "The teams' arguments",
    gameScreenReferee: "The referee is reviewing",
    gameScreenRound: "Round result",
    gameScreenEnd: "Game over",
    gameScreenReflect: "Final reflection",
    gameScreenLoading: "Loading the session",
    gameScreenError: "The session could not be opened",
    gameScreenResume: "Game in progress",
    sessionScreenIdentify: "Sign in to play",
    sessionScreenMain: "Ask the referee",
    sessionScreenSummary: "Session summary",
    pwaClose: "Close the install notice",
    pwaInstallText: "Install as app",
    correctSaved: "Correction saved",
    // Errores del servidor: el texto sale de details.motivo (ver utils/arbitroErrores.ts); si falta, se muestra el mensaje en español.
    errorMotivo_login_requerido: "You need to sign in with Google to do this.",
    errorMotivo_sesion_no_encontrada: "We could not find the session. Check the link.",
    errorMotivo_sesion_expirada: "This session has expired. Ask your teacher for a new link.",
    errorMotivo_sesion_de_otro_docente: "This session belongs to another teacher.",
    errorMotivo_sesion_id_invalido: "The session ID is not valid.",
    errorMotivo_datos_incompletos: "Some information is missing. Check the fields and try again.",
    errorMotivo_sin_consultas: "The session has no referee consultations yet.",
    errorMotivo_ronda_no_encontrada: "We could not find that round.",
    errorMotivo_pregunta_longitud: "The question must be between 3 and 500 characters long.",
    errorMotivo_preguntas_propias_desactivadas: "Custom questions are not allowed in this session.",
    errorMotivo_ia_no_configurada: "The referee is not available right now. Please try again later.",
    errorMotivo_solo_admin: "Only the administrator can do this.",
    errorMotivo_error_interno: "Something went wrong. Please try again.",
    errorMotivo_nota_requerida: "A note is required to correct a verdict.",
    errorMotivo_veredicto_requerido: "A verdict is required to correct this.",
    errorMotivo_observacion_no_encontrada: "We couldn't find that AI observation. It may have already been updated: reload the page.",
    pageTitleSuffix: "The Traffic Light Game",
    gradeCompleta: "Complete",
    gradeIncompleta: "Incomplete",
    gradeIncorrecta: "Incorrect",
    pointsText: (valor: string, n: number): string => (n === 1 ? `${valor} point` : `${valor} points`),
    pointsUnit: "points",
    jgCheckLink: "Check the link with your teacher.",
    jgSlow: "The referee is taking longer than usual. It may be your internet connection. Please try again.",
    jgTeamN: "Team {n}",
    jgTeamNameLabel: "Team name *",
    jgTeamNamePlaceholder: "E.g. The Storytellers, Team A...",
    jgMembersLabel: "Members *",
    jgMembersPlaceholder: "E.g. John, Mary, Peter",
    jgStudentN: "Student {n}",
    jgStudentPlaceholder: "Name or initial",
    jgAddStudent: "Add student",
    jgAddStudentTo: "Add student to team {n}",
    jgRemoveStudent: "Remove",
    jgRemoveStudentLabel: "Remove student {n} from {team}",
    jgMembersNotice: "You can add students to the team; two per team is recommended.",
    jgMembersNoticeInd: "The metacognition report will be individual: each student answers on their turn. You can type the full name or just the initial.",
    jgHowManyQuestions: "How many questions will you play in total?",
    jgTotalHint: "Total number of questions · You can add more at the end",
    jgStart: "⚔️ Start the game!",
    jgQuestionOf: "Question {n} of {total}",
    jgAsksLabel: "Asks",
    jgAnswersLabel: "Answers",
    jgQuestionLabel: "Question",
    jgTimeLeft: "⏱️ Time left",
    jgTimeElapsed: "⏱️ Time elapsed",
    jgPickFromList: "📋 {team} — pick a question from the list:",
    jgWriteOwn: "✏️ Write my own question",
    jgWriteOwnLabel: "✏️ {team} — write the question you will ask",
    jgWriteOwnPlaceholder: "Write the question you will ask...",
    jgConfirmQuestion: "✅ Confirm question",
    jgPickFromListBack: "← Choose from the list",
    jgChosenTitle: "📋 Chosen question — read it aloud",
    jgChooseAnother: "← Choose another question",
    jgAnswersAloud: "answers aloud. When you finish, press the button.",
    jgAnswered: "✅ The team has answered — grade it",
    jgWriteAnswerLabel: "✏️ {team} — write the answer",
    jgWriteAnswerPlaceholder: "Type the team's answer here...",
    jgConfirmAnswer: "✅ Confirm answer",
    jgPass: "⏭️ Skip this question (no points)",
    jgAnswerOf: "{team}'s answer",
    jgIsComplete: "{team} — is the answer complete?",
    jgWhatMissing: "✏️ {team} — what was missing from the answer?",
    jgWhatMissingPlaceholder: "Explain what was missing or wrong, in as much detail as possible...",
    jgContinue: "Continue →",
    jgGradedAs: "{team} graded the answer as",
    jgForTeam: "For team {team}",
    jgSaysMissing: "{team} says it was missing:",
    jgAccepts: "Does {team} accept this grade?",
    jgYesAccept: "✅ Yes, they accept",
    jgConsult: "⚖️ Ask the referee",
    jgDisputeTitle: "⚖️ Referee consultation",
    jgDisputeHint: "Fill in the four fields so the referee can give a fair verdict.",
    jgCriteriosTitle: "Grading criteria",
    jgCriteriosCompactTitle: "To answer, keep in mind:",
    jgWhatQuestion: "📋 What was the question?",
    jgWhatQuestionPlaceholder: "Write the question the team asked...",
    jgWhatAnswered: "💬 What did {team} answer?",
    jgWhatAnsweredPlaceholder: "Summarize the team's answer...",
    jgWrittenAnswerLabel: "📝 Answer already written by {team}:",
    jgWhyGraded: "🗣️ Why did {team} grade it that way?",
    jgWhyGradedPlaceholder: "Why does {team} think the answer was {grade}?",
    jgWhyNotAccept: "🗣️ Why doesn't {team} accept that grade?",
    jgWhyNotAcceptPlaceholder: "What does {team} argue to claim it deserves more points?",
    jgBack: "← Back",
    jgRetry: "🔄 Try again",
    jgAnalyzing: "The referee is analyzing…",
    jgOneMoment: "One moment",
    jgVerdictTitle: "⚖️ Referee's verdict",
    jgThink: "💭 Food for thought:",
    jgSplitTitle: "This is how the point for this round is split:",
    jgSumInPlay: (valor: string, n: number): string => `Total: ${n === 1 ? `${valor} point` : `${valor} points`} of 1 at stake`,
    jgConfirmContinue: "✅ Confirm and continue",
    jgLastRound: "Last round",
    jgDidNotAnswer: "{team} did not answer this question.",
    jgPassedNoPoints: "⏭️ Question skipped — no points",
    jgResponded: "answered",
    jgGradeLabel: "Grade:",
    jgValidatedByReferee: " · ⚖️ Validated by the referee",
    jgSeeFinal: "🏆 See final result",
    jgNext: "▶️ Next — question {n} of {total}",
    jgWon: "{team} wins!",
    jgTie: "It's a tie!",
    jgRoundSummary: "Round summary",
    jgRoundLine: "Round {n}: {from} → {to} · {grade} · {points}",
    jgAddFive: "➕ Add 5 more questions",
    jgDoReport: "✍️ Complete the metacognition report",
    jgMetaQ1: "What did you learn today about \"{tema}\"?",
    jgMetaQ2: "When were you most unsure? How did you resolve it?",
    jgMetaQ3: "Describe a dispute where the referee (AI) stepped in: what was your position before and after? Did you agree with its verdict, or was there something it couldn't evaluate well?",
    jgMetaQ4: "How would you explain to a classmate what you learned today?",
    jgMetaIndTurn: "{alumno}'s turn ({k} of {n})",
    jgMetaIndIntro: "This reflection is individual: each member answers on their turn, on the same computer. When they finish, pass the computer to the next person. If someone is missing, you can skip their turn.",
    jgMetaIndNext: "Next: {alumno}",
    jgMetaIndSkip: "Skip {alumno}'s turn",
    jgReportSent: "Report sent!",
    jgReportSentDesc1: "The teacher can see the results",
    jgReportSentDesc2: "and both teams' reflections.",
    jgCloseTab: "📱 You can close this tab.",
    jgTeacherSees: "The teacher will see the report in their dashboard.",
    jgSendManually: "You can also send it to the teacher manually",
    jgCopiedShort: "✅ Copied!",
    jgCopyReport: "📋 Copy report",
    jgDownloadTxt: "⬇️ Download .txt",
    jgManualHint: "Copy the text and paste it as a private comment on the Classroom assignment, or download it and send it to the teacher.",
    jgReportOf: "✍️ Report from {team}",
    jgNextReport: "Next — report from {team} →",
    jgSending: "📤 Sending…",
    jgRetrySend: "🔁 Try sending again",
    jgSendReport: "📤 Send report to the teacher",
    jgReportSendError: "The report could not be sent. Check your connection and try again.",
    sesVerdictA: "✅ Position A is correct",
    sesVerdictB: "✅ Position B is correct",
    sesVerdictBothPartial: "⚖️ Both positions are partly correct",
    sesVerdictNone: "🔄 Neither position is entirely correct",
    sesErrFillAll: "Fill in all three fields: the conflict and the two positions.",
    sesErrSamePositions: "The two positions must be different. If the group agrees, you don't need a referee!",
    sesErrConsult: "Error consulting the referee: {detail}",
    sesErrName: "Please write your name or your group's name.",
    sesSummaryTitle: "INTERACTIONS WITH THE REFEREE",
    sesSummaryTeam: "Team: {team} — ",
    sesSummarySubject: "Subject: {materia} · Topic: {tema}",
    sesSummaryQuery: "Query {n}:",
    sesSummaryDisagreement: "Disagreement: {text}",
    sesSummaryPosA: "Position A: {text}",
    sesSummaryPosB: "Position B: {text}",
    sesSummaryVerdict: "Referee's verdict: {text}",
    sesSummaryExplanation: "Explanation: {text}",
    sesSummaryFooter: "Use these interactions to answer the report questions in Classroom.",
    sesLoading: "⏳ Loading session…",
    sesUnavailable: "Session not available",
    sesExpired: "This session has expired. Ask your teacher to create a new one.",
    sesNotFound: "This session doesn't exist. Check the link with your teacher.",
    sesTitle: "Learning Referee",
    sesIdentifyIntro: "Before you start, write your name so the teacher can see who took part.",
    sesNameLabel: "Your name or your group's names",
    sesNamePlaceholder: "E.g. John Smith, or John and Mary",
    sesTeamLabel: "Team name",
    sesOptional: "(optional)",
    sesTeamPlaceholder: "E.g. Team 1, The Biochemists...",
    sesEnter: "⚖️ Enter the Referee →",
    sesRefereeOf: "{materia} Referee",
    sesHideInstr: "▲ Hide instructions",
    sesShowInstr: "📋 See how it works →",
    sesHowTitle: "📋 How this session works",
    sesHowIntro: "Use the material your teacher gave you to work with your classmates. Discuss, quiz each other and reach agreements among yourselves —",
    sesHowIntroStrong: "that exchange is where you will learn the most",
    sesHowPlay: "How to play:",
    sesStep1a: "Form groups of",
    sesStep1b: "3 to 6 people",
    sesStep1c: ". You can make 2 or 3 teams, or compete individually.",
    sesStep2: "Ask each other questions about the topic.",
    sesStep3: "When someone answers correctly, that team or person scores 1 point.",
    sesStep4: "Keep a record of the disputes and how you resolved them.",
    sesStep5: "If the whole group has a genuine disagreement it can't resolve, only then use the referee.",
    sesStep6a: "At the end of class, the team or person with the most points",
    sesStep6b: "wins!",
    sesBeforeTitle: "⚠️ Before consulting the referee:",
    sesBefore1: "You must have tried to settle the disagreement among yourselves.",
    sesBefore2: "Each position is written by the student who defends it, in their own words.",
    sesBefore3: "The referee doesn't solve whole exercises — it only settles genuine disagreements.",
    sesMaterial: "📄 Working material",
    sesConsultTitle: "⚖️ Ask the referee",
    sesConsultHint: "Only when the whole group has a genuine disagreement about",
    sesConflictLabel: "What is the disagreement or the question in dispute?",
    sesConflictPlaceholder: "E.g. \"What is the correct answer and why?\"",
    sesPosALabel: "Position A —",
    sesPosAHint: "write it yourself",
    sesPosBLabel: "Position B —",
    sesPosBHint: "your classmate writes it",
    sesPosPlaceholder: "Write your answer or explanation...",
    sesAnalyzing: "The referee is analyzing the positions…",
    sesConsulting: "⏳ Consulting…",
    sesAskVerdict: "⚖️ Ask the referee for a verdict",
    sesWhy: "Why",
    sesKeepThinking: "💭 Food for thought",
    sesPosAUpper: "POSITION A",
    sesPosBUpper: "POSITION B",
    sesNewQuery: "New question for the referee",
    sesViewMine: "📋 See my interactions for the report ({n})",
    sesMineTitle: "📋 Your interactions with the referee",
    sesClassroomStrong: "📝 For your Classroom report:",
    sesClassroomText: "these are the interactions you had with the referee. Copy them and use them as input to answer the report questions your teacher left in the Classroom assignment.",
    sesQueryN: "Query {n}",
    sesDisagreementLabel: "Disagreement: ",
    sesCopied: "✅ Copied! Paste it into your Classroom submission",
    sesCopy: "📋 Copy summary for Classroom",
    sesCopiedAnnounce: "Copied! Paste it into your Classroom submission",
    sesFooter: "{materia} Referee · {tema} · Session valid until {hora}",
    areaNames: { "Ciencias Exactas y Naturales": "Exact and Natural Sciences", "Ciencias Sociales y Humanidades": "Social Sciences and Humanities", "Comunicación y Lenguajes": "Communication and Languages", "Arte y Expresión": "Art and Expression" },
    materiaNames: { "Matemática": "Mathematics", "Ciencias Naturales": "Natural Sciences", "Biología": "Biology", "Física": "Physics", "Química": "Chemistry", "Tecnología": "Technology", "Informática": "Computer Science", "Estadística": "Statistics", "Ciencias Sociales": "Social Studies", "Historia": "History", "Geografía": "Geography", "Formación Ética y Ciudadana": "Ethics and Civics", "Filosofía": "Philosophy", "Psicología": "Psychology", "Economía": "Economics", "Derecho": "Law", "Antropología": "Anthropology", "Lengua y Literatura": "Language and Literature", "Inglés": "English", "Portugués": "Portuguese", "Francés": "French", "Comunicación": "Communication", "TIC y Medios": "ICT and Media", "Literatura": "Literature", "Plástica / Artes Visuales": "Visual Arts", "Música": "Music", "Teatro": "Theater", "Educación Física": "Physical Education", "Danza": "Dance", "Artesanía y Diseño": "Crafts and Design" , "Plástica": "Visual Arts", "Lengua": "Language" },
    levelNames: { "Primaria": "Primary", "Secundaria": "Secondary" },
    gradeLabel: (g: string): string => `Grade ${parseInt(g, 10) || g}`,
    cfgHide: "▲ hide",
    cfgShowEdit: "▼ view / edit",
    cfgAdd: "▼ add",
    cfgErrPickSubject: "Choose the subject and write the topic before generating questions.",
    cfgErrGenQuestions: "The questions could not be generated: {detail}",
    cfgTypeMemory: "🧠 Recall",
    cfgTypeMixed: "🔀 Mixed",
    cfgTypeSkills: "🎯 Skill-building",
    cfgTypeMemoryHint: "Ask the team to recall a fact, a definition, or a specific detail.",
    cfgTypeMixedHint: "Combine recall and skill-building questions in the same bank.",
    cfgTypeSkillsHint: "They ask the team to compare, argue or spot a mistake instead of just recalling a fact — they give the referee more to work with.",
    cfgTypeSuggestionOral: "In oral mode, skill-building questions tend to work best.",
    cfgTypeSuggestionEscrita: "In written mode, recall questions tend to work best.",
    cfgTypeReviewEdit: "Review and edit the generated questions before playing.",
    cfgMissingBoth: "You haven't added questions or a base text yet.",
    cfgMissingText: "You haven't added a base text yet.",
    cfgMissingQuestions: "You haven't added questions yet.",
    cfgErrArea: "Choose an area.",
    cfgErrMateria: "Choose a subject.",
    cfgErrTema: "Write the session topic.",
    cfgErrLogin: "You need to sign in with Google to create the session.",
    cfgErrCreate: "Error creating the session: {detail}",
    cfgErrLogout: "Could not sign out.",
    cfgMySessions: "📊 My sessions",
    cfgLoggingOut: "Signing out…",
    cfgLogout: "Sign out",
    cfgRefereeOf: "Referee for",
    cfgAreaLegend: "Area of knowledge",
    cfgAreaShort: "Area",
    cfgAreaChange: "Change",
    cfgAreaChangeLabel: "Change the knowledge area",
    cfgLevel: "Level",
    cfgGradeYear: "Year / Grade",
    cfgSubject: "Subject",
    cfgNoSubjects: "There are no subjects for {level} in this area.",
    cfgTopic: "Session topic",
    cfgTopicPlaceholder: "Write the topic you will work on today...",
    cfgDuration: "Session length",
    cfgDurClass: "⏱️ Class",
    cfgDurClassDesc: "Expires in 3 hours",
    cfgDurDay: "📅 Day",
    cfgDurDayDesc: "Expires in 24 hours",
    cfgRoundTime: "Time per round",
    cfgNoLimit: "No limit",
    cfgMinutes: "{n} min",
    cfgRoundTimeHint: "Runs from question to answer; the referee isn't timed. When time runs out, teams can keep going or skip the question.",
    cfgSessionLang: "Game language",
    cfgSessionLangHint: "This is the language the students play in and the referee answers in.",
    cfgSessionOptions: "Session options",
    cfgOptsLeaveAsIs: "You can leave them as they are.",
    cfgOptsChange: "Change",
    cfgOptsHide: "Hide",
    cfgOptsSummaryClass: "Class (3 h)",
    cfgOptsSummaryDay: "Day (24 h)",
    cfgOptsSummaryNoRoundLimit: "No time limit per round",
    cfgOptsSummaryRoundMin: "{n} min per round",
    cfgOptsSummaryOwnYes: "Custom questions: yes",
    cfgOptsSummaryOwnNo: "Custom questions: no",
    cfgOptsSummaryMetaGroup: "Group metacognition",
    cfgOptsSummaryMetaIndividual: "Individual metacognition",
    cfgOptsSummaryMetaQEdited: "Metacognition questions edited",
    cfgOptsSummaryNoCriteria: "No criteria",
    cfgOptsSummaryOneCriterion: "1 criterion",
    cfgOptsSummaryCriteriaN: "{n} criteria",
    cfgUiLang: "Interface language",
    cfgLangEs: "Español",
    cfgLangEn: "English",
    cfgBaseText: "📖 Session base text",
    cfgRecommended: "(recommended)",
    cfgTextLiveTitle: "During the game:",
    cfgTextLiveWithout: "Without the text (after the pre-reading)",
    cfgTextLiveWith: "With the text printed",
    cfgTextLiveWithoutDesc: "They practice recalling what they read; the referee shows them the evidence afterward.",
    cfgTextLiveWithDesc: "They practice finding and citing evidence — a good fit for development-type questions.",
    cfgBaseTextPlaceholder: "Paste here the text the teams will work on. The referee will rule by quoting this text.",
    cfgBaseTextHelp: "With a base text, the referee and the AI judge answers against what was covered in class. Without one, they use general knowledge: it works, but it's less precise.",
    cfgBeforePlayStrong: "Before playing:",
    cfgBeforePlayA: "set aside time for close reading — as a group or in pairs. During the game the text",
    cfgBeforePlayStrong2: "will not be available on screen",
    cfgBeforePlayB: ". It's best to have it printed.",
    cfgCharCount: (n: string): string => `${n} / 8,000 chars`,
    cfgCutWarning: "The text will be cut off when it is sent to the referee",
    cfgGameQuestions: "📋 Questions for the game",
    cfgQuestionsHint1: "Each turn, the asking team picks one from this list. Used questions are marked.",
    cfgQuestionsHint2: "💡 You can project it from your dashboard for the pre-reading.",
    cfgQuestionsHint2ConTexto: "💡 You can project it from your dashboard for the pre-reading and during the game too.",
    cfgQuestionsUnit: "questions",
    cfgGenerating: "⏳ Generating…",
    cfgGenerateBtn: "✨ Generate with AI",
    cfgQuestionsPlaceholder: "Paste the questions for the game, or generate them with AI above. One per line — the asking team picks one from this list each turn.",
    cfgCreatingSession: "Creating session…",
    cfgSessionCreated: "✅ Session created",
    cfgExpires: "Expires: {date}",
    cfgOneLink: "👥 It's a single link for the whole class: each pair of students opens it on their own device and forms their team — there's no need to create a session for each pair.",
    cfgCopied: "✅ Copied",
    cfgCopyLink: "📋 Copy link",
    cfgShareClassroom: "📚 Share in Classroom",
    cfgSeeHistory: "📊 See session history →",
    cfgPostInstr: "📋 Instructions for students",
    cfgPostInstrDesc: "This is what gets sent to Classroom and printed — edit it if you want to change something.",
    cfgPostQuestionsDesc: "Optional. It doesn't appear on the game screen — hand it out beforehand through Classroom or the PDF.",
    cfgPostReport: "✍️ Final report (metacognition)",
    cfgPostReportDesc: "The questions each team answers when the game ends.",
    cfgPrint: "🖨️ Print / Save PDF",
    cfgPrintHint: "Prints the content above (instructions, questions and report) — students already get the instructions through Classroom, this is only if you also want a paper copy.",
    cfgCreateAnother: "Create another session",
    cfgLoginNotice: "🔐 We'll ask you to sign in with Google when you create the session, so the link, the report and the results email are tied to you.",
    cfgCreating: "⏳ Creating…",
    cfgGenerateSession: "⚖️ Create session",
    cfgLoginAndGenerate: "🔐 Sign in and create session",
    cfgMissingAreaLabel: "Area",
    cfgMissingMateriaLabel: "Subject",
    cfgMissingTemaLabel: "Session topic",
    cfgMissingToGenerate: (lista: string): string => `Missing: ${lista}`,
    cfgFormativeNote: "This is a formative practice: the score is part of the game, not a grade.",
    cfgWarnGenerateDesc: "The referee works best when it has a text to quote and questions based on that text. You can add them now or create the session anyway.",
    cfgAddQuestions: "← Add questions",
    cfgGenerateAnyway: "Create session anyway →",
    cfgNoBaseTitle: "You haven't added the base text yet",
    cfgNoBaseDesc: "The referee aims to improve reading comprehension: it works best when the team has read the text before playing. Spending a few minutes on it before starting helps a lot.",
    cfgLoadBaseText: "← Add base text",
    cfgGenerateQuestionsAnyway: "Generate anyway →",
    cfgNotSharedTitle: "You haven't shared the game with your students yet",
    cfgNotSharedDesc1: "The PDF has the instructions for students, but they will also need the link to join the game.",
    cfgNotSharedDesc2a: "The simplest way is to share it through",
    cfgNotSharedDesc2b: "— one click and the link reaches all students automatically, along with the instructions.",
    cfgShareByClassroom: "📚 Share through Classroom",
    cfgDownloadAnyway: "Download PDF anyway",
    cfgWarnShareDesc: "The referee works best when it has a text to quote and questions based on that text. You can add them now or share the session anyway.",
    cfgShareAnyway: "Share anyway →",
    cfgPublishClassroom: "📚 Post to Google Classroom",
    cfgTaskCreated: "Assignment created in Classroom!",
    cfgTaskCreatedDesc: "Students can now see it in their class.",
    cfgClose: "Close",
    cfgRetryPlain: "Try again",
    cfgLoadingCourses: "⏳ Loading your classes...",
    cfgNoCourses: "No active classes were found in your account.",
    cfgPickCourse: "Choose the class where you want to post the assignment:",
    cfgCreatingTask: "⏳ Creating assignment...",
    cfgHowTitle: "How does it work?",
    cfgTapToSee: "— tap to see",
    cfgHow1T: "Prior reading",
    cfgHow1W: "All students",
    cfgHow1D: "Before playing, they read the base text the teacher handed out. During the game the text is not available on screen.",
    cfgHow1DConTexto: "Before playing, they read the base text the teacher handed out. During the game they also have it printed to check back on.",
    cfgHow2T: "The turn — question and answer",
    cfgHow2W: "Team A asks · Team B answers",
    cfgHow2D: "The asking team picks a question from the list and asks it aloud to the other team. The other team answers out loud or in writing, depending on the chosen answer mode.",
    cfgHow3T: "Grading",
    cfgHow3W: "The team that asked grades",
    cfgHow3D: "They decide whether the answer was complete (1 point), incomplete (0.5 points) or incorrect (0 points). If the teacher loaded grading criteria for the session, those criteria define what counts as a complete answer.",
    cfgHow4T: "Is the grade accepted?",
    cfgHow4W: "The team that answered decides",
    cfgHow4D: "If they accept, the point is awarded and the game goes on. If not, both teams argue in writing and call the referee.",
    cfgHow5T: "The referee steps in",
    cfgHow6T: "Metacognition",
    cfgHow6W: "Both teams",
    cfgHow6D: "When the game ends, each team completes a report that reaches the teacher's dashboard automatically.",
    cfgHowNoteStrong: "How the teacher receives the results:",
    cfgHowNote: "when the game ends, each team presses the button to send the report and the metacognition answers reach your dashboard on their own, without them having to send you anything. Just in case, the game also lets them copy or download that text. In written mode, the AI automatically reviews the answers that had no dispute and flags the ones worth your review; once the session is played, you can generate a PDF to share with the class.",
    cfgFormTitleDoc: "Set up session",
    landDesc_exactas: "Students debate in groups about the topic you choose. The AI steps in only when two students hold different positions they can't settle on their own — it doesn't explain or solve, it only rules on the disagreement.",
    landEx_exactas: "Equations, fractions, functions, statistics, chemical reactions, physics problems…",
    landDesc_sociales: "Students debate in groups about sources, interpretations or concepts. The AI doesn't give opinions or explanations — it only rules when the group has a genuine disagreement it can't settle on its own.",
    landEx_sociales: "Historical sources, philosophical concepts, economic data, legal situations…",
    landDesc_lenguajes: "Students work in groups on texts, prompts or productions. The AI rules on genuine disagreements about interpretation, grammar or language use — without replacing the debate among peers.",
    landEx_lenguajes: "Text analysis, grammar in context, literary interpretation, oral production…",
    landDesc_arte: "Students debate in groups about works, techniques or expressive concepts. The AI rules on specific disagreements — the exchange among students stays at the center.",
    landEx_arte: "Analysis of works, musical styles, game rules, concepts of expression…",
    landDone: "✅ When the game ends, each team presses \"Send report\" inside the game. The teacher sees it automatically on their dashboard.",
    landErrLogin: "Could not sign in. Please try again.",
    landErrLogout: "Could not sign out. Please try again.",
    landLoggingIn: "⏳ Signing in…",
    landUsedBefore: "🔑 I've used it before",
    landConfigure: "⚖️ Set up a session →",
    landConfigureMine: "⚖️ Set up my session →",
    landFree: "Free · You only need one device per group",
    landSubjects: "Subjects available in this area",
    landExamples: "Topic examples:",
    infTitle: "Learning Referees",
    infIntro: "Group activities where the AI acts as a referee — it doesn't explain or solve, it only steps in when the group has a genuine disagreement it can't settle on its own.",
    infSteps: "🪜 See the steps of the activity",
    infStep1: "The teacher signs in with Google, sets the topic, and the session is generated in seconds.",
    infStep2: "Students form 2 teams and open the link on the device of one member of each team.",
    infStep3: "One team asks the other an oral question; the team that answers does so aloud.",
    infStep4: "The team that asked grades the answer: complete (1 point), incomplete (0.5) or incorrect (0). The game adds up the points automatically.",
    infStep6: "When the game ends, each team completes a metacognition report inside the game. The group is the protagonist.",
    infPerArea: "One referee per area",
    infConfigure: "Set up →",
    infWhat: "What does this referee do?",
    infMaterias_exactas: "Mathematics, Physics, Chemistry, Biology, Statistics",
    infMaterias_sociales: "History, Geography, Philosophy, Economics, Law",
    infMaterias_lenguajes: "Language, English, Literature, Communication",
    infMaterias_arte: "Visual Arts, Music, Theater, Physical Education",
    infWhat_exactas: "The Exact Sciences referee settles disagreements about procedures, results and concepts in mathematics and natural sciences. When two students can't agree on whether a calculation is right, whether a definition is correct or whether a logical argument makes sense, the referee analyzes both positions and gives a well-grounded verdict. It doesn't solve the exercise for the group — it only steps in for a genuine disagreement the group can't settle on its own.",
    infWhat_sociales: "The Social Sciences referee rules on disagreements about historical interpretations, economic concepts, geographic analysis or philosophical and legal debates. In subjects where many answers aren't black and white, the referee evaluates whether the arguments are well founded and the positions conceptually sound, without imposing a single truth but guiding the group toward greater precision.",
    infWhat_lenguajes: "The Languages referee settles disagreements about grammar, vocabulary, text interpretation and correct language use. If the group argues about the correct form of a grammatical construction, the meaning of a word in context or the interpretation of a literary paragraph, the referee analyzes both positions using clear linguistic and communicative criteria.",
    infWhat_arte: "The Art referee rules on disagreements about concepts, techniques and aesthetic criteria in artistic and expressive disciplines. When the group can't agree on whether a technique is correct, what characterizes a style, or how to interpret a work, the referee applies criteria specific to each discipline to guide the debate toward greater conceptual depth.",
    infSemaforo: "🚦 What about the Traffic Light Game?",
    infSemName: "The Traffic Light Game",
    infSem1b: " is the other proposal on this platform. It is also built on coopetition (collaboration + competition), but the whole class plays it, in stages: first in teams, then all together.",
    infSem2: "They are independent: you can use the Learning Referees, the Traffic Light Game, or both.",
    infFreeNoReg: "Free · No sign-up · One device per group",
    // Portada pública /arbitros
    pubLogin: "Sign in",
    pubDemoCta: "▶ Watch the demo",
    pubHowCta: "How it works ↓",
    pubHowTitle: "How it works",
    pubDemoTitle: "Demo",
    pubDemoSoonTitle: "Demo video",
    pubDemoSoonText: "A complete session, start to finish, will be available here very soon.",
    pubVideoFrameTitle: "Learning Referees demo video",
    pubMoreInfo: "More about the referees →",
    pubTryClass: "I want to try it with my class",
    pubVideoYoutube: "Can’t see the video? Open it on YouTube",
    pubNewTab: "(opens in a new tab)",
    infBack: "← Back to home",
    landPageTitle: "Learning Referee",
    panPageTitle: "Teacher dashboard",
    panLevelShallow: "Shallow",
    panLevelAdequate: "Adequate",
    panLevelDeep: "Deep",
    panBackToList: "← Back to dashboard",
    panDownloadPdf: "🖨️ Download report (PDF)",
    panPrintThisGame: "🖨️ Print this game",
    // Word used in the PDF file name (no accents or spaces needed: paraNombreArchivo cleans it up anyway).
    panPdfJuego: "Referees",
    panShowText: "📖 Show text to students",
    panShowTextHint: "Opens full screen for projecting",
    panGenerating: "Generating teaching report…",
    panAnalyzing: "We are analyzing the session's disputes",
    panUpdateReport: "🔄 Update report",
    panNewQueries: "⏳ There are new referee consultations since this report was generated — some groups may be missing.",
    panDebateLevel: "Debate level",
    panExecSummary: "📋 Executive summary",
    panConcepts: "💡 Concepts debated",
    panStrengths: "💪 Strengths",
    panOpportunities: "🎯 Areas of opportunity",
    panPatterns: "🔍 Observed patterns",
    panRecommendations: "📌 Recommendations for the teacher",
    panRetake: "❓ Questions to revisit in class",
    panPassed: "⏭️ Skipped questions",
    panPassedBy: "Skipped by {team}",
    panAnotherTeam: "a team",
    panPassedTime: " · {time} min",
    panNoText: "(no text)",
    panPassedNote: "These questions were not included in the teaching analysis.",
    panReflectionsTitle: (n: number): string => (n > 0 ? `Team reflections (${n} ${n === 1 ? "game" : "games"})` : "Team reflections"),
    panLoadingReflections: "Loading reflections…",
    panNoReflections: "No metacognition reports have been sent yet.",
    panQ1: "What did they learn?",
    panQ2: "Hardest moment?",
    panQ3: "Agreement with the referee?",
    panQ4: "What would they teach someone else?",
    panQ1s: "What did they learn?",
    panQ4s: "What would they teach someone else?",
    panWentToReferee: "⚖️ Went to the referee",
    panCloseX: "✕ Close",
    panTitle: "⚖️ Teacher dashboard",
    panSubtitle: "Your learning referee sessions",
    panAdminLoading: "⏳ Loading...",
    panAdminBack: "👤 See only my sessions",
    panAdminAll: "🔐 See all sessions (Admin)",
    panAdminShowing: "Showing sessions from all teachers",
    panLoadingSessions: "⏳ Loading sessions…",
    panNoSessions: "You don't have any sessions yet.",
    panNoSessionsHint: "Set up a referee and share it with your students.",
    panGamesCount: (n: number): string => `🎮 ${n} ${n === 1 ? "game" : "games"}`,
    panWithReport: "✅ With report",
    panNoReport: "No report yet",
    panGroupReading: "📖 Group reading before playing",
    panViewReport: "View report",
    panGenerateReport: "Generate report",
    docInstrToday: "Today we are going to practice: {t}",
    docInstrBefore: "📖 Before playing: take a few minutes to read the topic's base text. If you do not have it at hand, ask the teacher to project it from their panel. During the game it will not be available on screen — the debate is based on what you understood.",
    docInstrBeforeConTexto: "📖 Before playing: take a few minutes to read the topic's base text. During the game you will also have it printed to check back on.",
    docInstrHow: "📋 How it works:",
    docInstrStep1: "1. Form 2 teams.",
    docInstrStep2: "2. Open this session's link on one member's device from each team — the whole round is played there.",
    docInstrStep3: "3. One team asks the other the question out loud; the team that answers also answers out loud.",
    docInstrStep3Escrita: "3. One team asks the other the question out loud; the team that answers writes their answer down.",
    docInstrStep4: "4. The team that asked grades the answer: complete (1 point), incomplete (0.5) or incorrect (0). The game adds up the points automatically.",
    docInstrStep6: "6. When finished, each team completes the metacognition reflection inside the game, which is sent to the teacher.",
    docInstrStep6Ind: "6. When you finish, each member of each team completes their own metacognition inside the game, taking turns on the same computer (a name or just an initial is fine). It is sent to the teacher.",
    docInstrCriteriaTitle: "For an answer to be complete, it has to meet these criteria from the teacher:",
    docIndStudent: "— {alumno}",
    docRepIntro: "When the game ends, each team writes:",
    docRep1: "1. What did you learn today about \"{t}\"?",
    docRep2: "2. When did you doubt the most? How did you resolve it?",
    docRep3: "3. Tell about a dispute where the referee stepped in: what was your position before that decision and what was it after? Did it help you see the topic from another point of view?",
    docRep4: "4. How would you explain to another classmate what you learned today?",
    docRepEnd: "When you finish, press the \"Send report to the teacher\" button inside the game. The report reaches the teacher's panel automatically — nothing else is needed.",
    docTitle: "⚖️ Learning Referee — {materia} · {tema}",
    docQuestionsHeader: "📋 QUESTIONS FOR THE GAME:",
    docFinalReportHeader: "✍️ FINAL REPORT:",
    docLinkLine: "🔗 Game link: {link}",
    docTeacherNote: "👩‍🏫 Teacher: when the teams finish playing you will be able to see their answers and the pedagogical report at https://thetrafficlightgame.com/arbitro-panel — sign in with the same Google account you used to create this assignment. Students do not have access to that view.",
    docClassroomTitle: "⚖️ Learning Referee — {materia}: {tema}",
    docPrintTitle: "Learning Referee: {materia}",
    docPrintInstr: "📋 Instructions for students",
    docPrintQuestions: "📋 Questions for the game",
    docPrintReport: "✍️ Final report",
    docSesionTitle: "SESSION REPORT — {materia} · {tema}",
    docDate: "Date: {fecha}",
    docTeam: "Team: {nombre} ({integrantes})",
    docFinalScore: (p: string, n: number): string => `Final score: ${p} ${n === 1 ? "point" : "points"}`,
    docReflectionsOf: "REFLECTIONS OF {nombre} ({integrantes})",
    docMetaQ1: "What did you learn today about \"{tema}\"?",
    docMetaQ2: "When did you doubt the most? How did you resolve it?",
    docMetaQ3: "Tell about a dispute where the referee (AI) stepped in: what was your position before and what was it after?",
    docMetaQ4: "How would you explain to another classmate what you learned today?",
    docFooter: "Learning Referee · thetrafficlightgame.com",
    docFileName: "referee-report",
    modelConflict: "Question: \"{pregunta}\"\n\nAnswer from {equipo}: \"{respuesta}\"",
    modelPositionB: "{equipo} (does not accept the grade): {argumento}",

    ingTitle: "Enter your session code",
    ingHint: "Ask your teacher for it.",
    ingLabel: "Session code",
    ingPlaceholder: "AB3XYZ",
    ingSubmit: "Join",
    ingVerificando: "Checking…",
    ingErrorNoEncontrada: "We couldn't find that code. Check that it's typed correctly.",
    ingErrorExpirada: "This session has expired. Ask your teacher for a new code.",
    ingErrorConexion: "No connection. Check your internet and try again.",

    proyButton: "Show code to the class",
    proyAddressLabel: "Go to:",
    proyCodeLabel: "Code:",
    proyQrHint: "Or scan the QR code",
    proyHint: "Each group joins with the same code, from their own device.",
    proyClose: "Close",
  },
} as const;

export type TranslationKeys = typeof en;
